import React, { FC, useEffect, useMemo, useState } from "react";
import axiosInstant from "../../../../api";
import { SingleFilter } from "./singleFilter";
import { ISingleOption, getFilterProps } from "./helper";
import { getProductCategories } from "../../../../api/endpoints";
import { IconCart } from "../../../../utils/icons";

interface IProductCategory {
  id: number;
  name: string;
  parent_id: number | null;
}

interface Props {
  onFilterSelect: (key: string, id?: string) => void;
}

/**
 * Filters videos by their associated product's category. Options are the full
 * product-category tree, labelled with their parent path (e.g.
 * "Electronics › Audio"); selecting a parent also matches products in its
 * descendant categories (expansion happens server-side).
 */
export const ProductCategoryFilter: FC<Props> = ({
  onFilterSelect,
  ...rest
}) => {
  const [categories, setCategories] = useState<IProductCategory[]>([]);

  useEffect(() => {
    axiosInstant
      .get(getProductCategories())
      .then((response) => {
        const rows: IProductCategory[] =
          response.data?.rows || response.data || [];
        setCategories(Array.isArray(rows) ? rows : []);
      })
      .catch((error) => {
        console.error(error.response);
      });
  }, []);

  const options = useMemo<Array<ISingleOption>>(() => {
    const byId = new Map<number, IProductCategory>();
    categories.forEach((c) => byId.set(c.id, c));

    const pathLabel = (c: IProductCategory): string => {
      const segs: string[] = [];
      let cur: IProductCategory | undefined = c;
      let depth = 0;
      while (cur && depth < 8) {
        segs.unshift(cur.name);
        cur = cur.parent_id != null ? byId.get(cur.parent_id) : undefined;
        depth += 1;
      }
      return segs.join(" › ");
    };

    return categories
      .filter((c) => c.name !== "Uncategorized")
      .map((c) => ({ id: String(c.id), name: pathLabel(c) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  return (
    <SingleFilter
      {...rest}
      {...getFilterProps(options, "productCategoryId")}
      updateSearch={onFilterSelect}
      icon={IconCart}
    />
  );
};
